/**
 * API 에러 처리 유틸리티
 */

export const ERROR_MESSAGES = {
  NETWORK_ERROR: '네트워크 연결을 확인해주세요.',
  SERVER_ERROR: '서버 오류가 발생했습니다.',
  UNAUTHORIZED: '인증이 필요합니다.',
  FORBIDDEN: '접근 권한이 없습니다.',
  NOT_FOUND: '요청한 리소스를 찾을 수 없습니다.',
  VALIDATION_ERROR: '입력 정보를 확인해주세요.',
  FILE_TOO_LARGE: '파일 크기가 너무 큽니다.',
  UNSUPPORTED_FILE: '지원하지 않는 파일 형식입니다.',
  UNKNOWN_ERROR: '알 수 없는 오류가 발생했습니다.'
};

/**
 * HTTP 상태 코드에 따른 에러 메시지 매핑
 */
export const getErrorMessageByStatus = (status) => {
  switch (status) {
    case 400:
      return ERROR_MESSAGES.VALIDATION_ERROR;
    case 401:
      return ERROR_MESSAGES.UNAUTHORIZED;
    case 403:
      return ERROR_MESSAGES.FORBIDDEN;
    case 404:
      return ERROR_MESSAGES.NOT_FOUND;
    case 413:
      return ERROR_MESSAGES.FILE_TOO_LARGE;
    case 415:
      return ERROR_MESSAGES.UNSUPPORTED_FILE;
    case 500:
    case 502:
    case 503:
    case 504:
      return ERROR_MESSAGES.SERVER_ERROR;
    default:
      return ERROR_MESSAGES.UNKNOWN_ERROR;
  }
};

/**
 * API 에러 처리 함수
 */
export const handleApiError = (error) => {
  console.error('[API_ERROR]', error);

  // 네트워크 오류 (서버에 연결할 수 없음)
  if (!error.response) {
    if (error.code === 'ECONNREFUSED' || error.message === 'Network Error') {
      return {
        message: ERROR_MESSAGES.NETWORK_ERROR,
        type: 'network',
        status: null
      };
    }
    return {
      message: ERROR_MESSAGES.UNKNOWN_ERROR,
      type: 'unknown',
      status: null
    };
  }

  // HTTP 응답 에러
  const status = error.response.status;
  const data = error.response.data;
  
  // 서버에서 제공한 에러 메시지가 있으면 사용
  const serverMessage = data?.message || data?.error;
  const fallbackMessage = getErrorMessageByStatus(status);

  return {
    message: serverMessage || fallbackMessage,
    type: 'http',
    status: status,
    details: data
  };
};

/**
 * 파일 업로드 에러 처리
 */
export const handleFileUploadError = (error, fileName = '') => {
  const errorInfo = handleApiError(error);
  
  // 파일 관련 특별 처리
  if (errorInfo.status === 413) {
    return {
      ...errorInfo,
      message: `파일 "${fileName}"이 너무 큽니다. 100MB 이하의 파일을 선택해주세요.`
    };
  }
  
  if (errorInfo.status === 415) {
    return {
      ...errorInfo,
      message: `파일 "${fileName}"은 지원하지 않는 형식입니다. Excel, CSV, PDF, DOC 파일만 업로드 가능합니다.`
    };
  }
  
  return errorInfo;
};

/**
 * 인증 에러 처리
 */
export const handleAuthError = (error) => {
  const errorInfo = handleApiError(error);
  
  if (errorInfo.status === 401) {
    // 토큰 만료 또는 유효하지 않음
    localStorage.removeItem('auth-storage');
    window.location.href = '/login';
    return {
      ...errorInfo,
      message: '로그인이 필요합니다. 로그인 페이지로 이동합니다.'
    };
  }
  
  return errorInfo;
};

/**
 * 사용자 친화적인 에러 알림 표시
 */
export const showErrorNotification = (error, title = '오류') => {
  const errorInfo = typeof error === 'string' ? { message: error } : handleApiError(error);
  
  // 브라우저 기본 alert 사용 (추후 토스트 라이브러리로 교체 가능)
  alert(`${title}: ${errorInfo.message}`);
  
  return errorInfo;
};

/**
 * 성공 메시지 표시
 */
export const showSuccessNotification = (message, title = '성공') => {
  alert(`${title}: ${message}`);
};

/**
 * 확인 대화상자
 */
export const showConfirmDialog = (message, title = '확인') => {
  return window.confirm(`${title}: ${message}`);
};

/**
 * 재시도 가능한 에러인지 확인
 */
export const isRetryableError = (error) => {
  const errorInfo = handleApiError(error);
  
  // 네트워크 오류나 서버 오류는 재시도 가능
  return errorInfo.type === 'network' || 
         (errorInfo.status >= 500 && errorInfo.status < 600);
};

/**
 * 개발 모드에서 상세 에러 로그
 */
export const logDetailedError = (error, context = 'API') => {
  if (process.env.REACT_APP_ENV === 'development') {
    console.group(`[${context}_ERROR_DETAILS]`);
    console.error('Error object:', error);
    console.error('Error message:', error.message);
    console.error('Error status:', error.response?.status);
    console.error('Error data:', error.response?.data);
    console.error('Error config:', error.config);
    console.groupEnd();
  }
};